import { z } from 'zod'

// Defaults are intentional: pagination should be predictable in URL-less states.
export const paginationSearchSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().default(25),
})

export function withPagination<T extends z.ZodRawShape>(extraSchema: z.ZodObject<T> | null) {
  if (extraSchema === null) {
    return paginationSearchSchema
  }
  return z.object({
    ...paginationSearchSchema.shape,
    ...extraSchema.shape,
  })
}

type PaginationMetaLike = {
  page: number
  limit: number
  total: number
}

export type RowIndexMode = 'ascending' | 'descending'

function getRowIndexConfig(options: {
  meta?: PaginationMetaLike | null
  page?: number
  limit?: number
  mode?: RowIndexMode
}): {
  rowIndexMode: RowIndexMode
  rowIndexOffset: number
  rowIndexTotal?: number | undefined
} {
  let rowIndexMode: RowIndexMode = 'ascending'
  if (options.mode) {
    rowIndexMode = options.mode
  }

  let rowIndexOffset = 0
  let rowIndexTotal: number | undefined = undefined
  const meta = options.meta
  if (meta !== null && meta !== undefined) {
    rowIndexOffset = (meta.page - 1) * meta.limit
    rowIndexTotal = meta.total
  } else {
    const page = options.page
    const limit = options.limit
    if (typeof page === 'number' && typeof limit === 'number') {
      rowIndexOffset = (page - 1) * limit
    }
  }

  return {
    rowIndexMode,
    rowIndexOffset,
    rowIndexTotal,
  }
}

export function getRowIndexProps(options: {
  meta?: PaginationMetaLike | null
  page?: number
  limit?: number
  mode?: RowIndexMode
}): {
  rowIndexMode: RowIndexMode
  rowIndexOffset: number
  rowIndexTotal?: number
} {
  const config = getRowIndexConfig(options)
  const props: {
    rowIndexMode: RowIndexMode
    rowIndexOffset: number
    rowIndexTotal?: number
  } = {
    rowIndexMode: config.rowIndexMode,
    rowIndexOffset: config.rowIndexOffset,
  }
  if (typeof config.rowIndexTotal === 'number') {
    props.rowIndexTotal = config.rowIndexTotal
  }
  return props
}
